import { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { isWebxrSupported } from "./webxrSupport";
import { AR_STRATEGY, resolveSpatialArStrategy, sceneDelivery } from "./canonicalAR";
import {
  isAndroidUA,
  isAppleMobileUA,
  launchAndroidSceneViewer,
  launchIosQuickLook,
} from "./nativeArLaunchers";

export default function useCanonicalArController({
  session = null,
  enabled = true,
  placementProfile = "floor",
  fallbackUrl = null,
  onAttempt = null,
  onNativeLaunch = null,
  onFailure = null,
  onExit = null,
} = {}) {
  const [webxrSupport, setWebxrSupport] = useState("checking");
  const [runtimeActive, setRuntimeActive] = useState(false);
  const [nativeFallback, setNativeFallback] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let live = true;
    setNativeFallback(null);
    setMessage("");
    setRuntimeActive(false);

    if (!enabled) {
      setWebxrSupport("fallback");
      return () => { live = false; };
    }

    setWebxrSupport("checking");
    isWebxrSupported()
      .then(ok => { if (live) setWebxrSupport(ok ? "supported" : "fallback"); })
      .catch(() => { if (live) setWebxrSupport("fallback"); });

    return () => { live = false; };
  }, [enabled, session?.doar?.id, session?.revisionId]);

  const delivery = useMemo(() => sceneDelivery(session), [session]);
  const hasGlb = Boolean(delivery.nativeGlbUrl);
  const hasUsdz = Boolean(delivery.nativeUsdzUrl);
  const parametricSpatial = Boolean(
    session?.assetContract?.parametric
    || session?.preview?.parametric
    || session?.doar?.semanticCategory === "window_covering"
  );
  const hasFirstPartySpatialVisual = Boolean(session && (delivery.hasWebxrModel || parametricSpatial));
  const supportKnown = webxrSupport !== "checking";

  const strategy = useMemo(() => {
    if (!enabled || !session) return AR_STRATEGY.UNSUPPORTED;
    if (!supportKnown && hasFirstPartySpatialVisual) return AR_STRATEGY.UNSUPPORTED;
    return resolveSpatialArStrategy({
      webxrSupported: webxrSupport === "supported" && session?.capabilities?.webXR !== false,
      hasFirstPartySpatialVisual,
      hasGlb,
      hasUsdz,
      isApple: isAppleMobileUA(),
      isAndroid: isAndroidUA(),
    });
  }, [enabled, session, supportKnown, hasFirstPartySpatialVisual, hasGlb, hasUsdz, webxrSupport]);

  const nativeFallbackStrategy = useMemo(() => {
    if (!enabled || !session) return AR_STRATEGY.UNSUPPORTED;
    return resolveSpatialArStrategy({
      webxrSupported: false,
      hasFirstPartySpatialVisual,
      hasGlb,
      hasUsdz,
      isApple: isAppleMobileUA(),
      isAndroid: isAndroidUA(),
    });
  }, [enabled, session, hasFirstPartySpatialVisual, hasGlb, hasUsdz]);

  const preparing = Boolean(
    enabled
    && session
    && !supportKnown
    && hasFirstPartySpatialVisual
  );
  const canStart = strategy !== AR_STRATEGY.UNSUPPORTED;

  const launchNative = useCallback((requested = nativeFallback || nativeFallbackStrategy) => {
    if (!session || requested === AR_STRATEGY.UNSUPPORTED) return false;
    const url = fallbackUrl || (typeof window !== "undefined" ? window.location.href : "");
    const launched = requested === AR_STRATEGY.IOS_QUICK_LOOK
      ? launchIosQuickLook(delivery.nativeUsdzUrl)
      : requested === AR_STRATEGY.ANDROID_SCENE_VIEWER
        ? launchAndroidSceneViewer(delivery.nativeGlbUrl, {
            fallbackUrl: url,
            placementProfile,
          })
        : false;

    if (launched) {
      setMessage("");
      setNativeFallback(null);
      onNativeLaunch?.(requested);
    } else {
      setMessage("AR could not open on this device.");
    }
    return launched;
  }, [delivery, fallbackUrl, nativeFallback, nativeFallbackStrategy, onNativeLaunch, placementProfile, session]);

  const handleFailure = useCallback((error) => {
    setRuntimeActive(false);
    if (nativeFallbackStrategy !== AR_STRATEGY.UNSUPPORTED) {
      setNativeFallback(nativeFallbackStrategy);
      setMessage("Live AR could not start. Open the native AR fallback.");
    } else {
      setNativeFallback(null);
      setMessage(error?.message || "AR could not start on this device.");
    }
    onFailure?.(error);
  }, [nativeFallbackStrategy, onFailure]);

  const handleExit = useCallback(() => {
    setRuntimeActive(false);
    onExit?.();
  }, [onExit]);

  const start = useCallback((runtimeRef) => {
    setMessage("");
    setNativeFallback(null);
    onAttempt?.();

    if (strategy === AR_STRATEGY.FIRST_PARTY_WEBXR) {
      const startRuntime = runtimeRef?.current?.start;
      if (typeof startRuntime !== "function") {
        setMessage("AR is still preparing. Try again in a moment.");
        return false;
      }

      // Inline Three/model-viewer content must be gone before requestSession,
      // while the original user gesture is still active.
      flushSync(() => setRuntimeActive(true));
      const pending = startRuntime();
      if (!pending) {
        setRuntimeActive(false);
        handleFailure(new Error("AR could not start on this device."));
        return false;
      }

      Promise.resolve(pending).then(ok => {
        if (ok === false) setRuntimeActive(false);
      }).catch(() => {
        // UniversalDoARSpace owns the runtime failure callback and fallback
        // decision. This controller only releases the inline renderer owner.
        setRuntimeActive(false);
      });
      return true;
    }

    if (strategy !== AR_STRATEGY.UNSUPPORTED) {
      return launchNative(strategy);
    }

    if (preparing) {
      setMessage("Preparing AR… try again in a moment.");
    } else {
      setMessage("AR is not available on this device for this DoAR.");
    }
    return false;
  }, [handleFailure, launchNative, onAttempt, preparing, strategy]);

  const stop = useCallback((runtimeRef) => {
    try { runtimeRef?.current?.stop?.(); } catch {}
    setRuntimeActive(false);
  }, []);

  return {
    strategy,
    nativeFallbackStrategy,
    webxrSupport,
    runtimeActive,
    nativeFallback,
    message,
    preparing,
    canStart,
    start,
    stop,
    launchNative,
    handleFailure,
    handleExit,
    setMessage,
  };
}
